// src/pages/HistorialSolicitudes.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast/useToast.jsx";
import { useAuth } from "../contexts/AuthContext";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, limit, deleteDoc, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";

export default function HistorialSolicitudes() {
  const { showToast } = useToast();
  const { user, companyId, role, isCompanyAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todas");
  const [sendingEmail, setSendingEmail] = useState(null); // ID de solicitud enviando email

  const isAdmin = isCompanyAdmin() || isSuperAdmin();

  // Cargar solicitudes
  useEffect(() => {
    loadSolicitudes();
  }, [user, companyId, role, filter]);

  const loadSolicitudes = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      console.log('🔍 Cargando solicitudes...');
      console.log('👤 User ID:', user.uid);
      console.log('🏢 Company ID:', companyId);

      // Admins ven todas las solicitudes de la empresa; usuarios solo las suyas
      let q;
      if (isAdmin && companyId) {
        q = query(
          collection(db, "work_requests"),
          where("companyId", "==", companyId)
          // SIN LÍMITE - traer todas las solicitudes
        );
      } else {
        q = query(
          collection(db, "work_requests"),
          where("requesterId", "==", user.uid)
          // SIN LÍMITE - traer todas las solicitudes del usuario
        );
      }

      console.log('📊 Ejecutando query simplificada...');
      const snapshot = await getDocs(q);
      console.log('📋 Documentos encontrados:', snapshot.docs.length);
      
      let results = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Ordenar en el cliente por fecha
      results.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime; // Más reciente primero
      });
      
      console.log('📄 Solicitudes cargadas:', results);

      // Filtrar por estado si no es "todas"
      if (filter !== "todas") {
        results = results.filter(s => s.status === filter);
        console.log('🔍 Después del filtro de estado:', results.length);
      }

      setSolicitudes(results);
    } catch (error) {
      console.error("❌ Error cargando solicitudes:", error);
      showToast({ 
        type: "error", 
        message: "Error cargando el historial de solicitudes" 
      });
    } finally {
      setLoading(false);
    }
  };

  // Eliminar solicitud (solo admins)
  const handleDelete = async (e, solicitudId) => {
    e.stopPropagation();
    if (!window.confirm('¿Seguro que quieres eliminar esta solicitud?')) return;
    try {
      await deleteDoc(doc(db, "work_requests", solicitudId));
      setSolicitudes(prev => prev.filter(s => s.id !== solicitudId));
      showToast({ type: "success", message: "Solicitud eliminada" });
    } catch (error) {
      showToast({ type: "error", message: "Error al eliminar la solicitud" });
    }
  };

  // 📧 Reenviar email de solicitud
  const handleReenviarEmail = async (e, solicitud) => {
    e.stopPropagation();
    
    if (!solicitud) {
      showToast({ type: 'error', message: 'Solicitud no encontrada' });
      return;
    }

    if (!window.confirm(`¿Reenviar email de la solicitud ${solicitud.numeroOferta || solicitud.id}?`)) {
      return;
    }

    setSendingEmail(solicitud.id);

    try {
      const currentUser = user || auth.currentUser;
      if (!currentUser) throw new Error('No hay usuario logueado');

      const idToken = await currentUser.getIdToken(true);
      const SEND_URL = import.meta.env.VITE_SEND_EMAIL_URL;
      if (!SEND_URL) throw new Error('URL de envío no configurada');

      const emailHtml = buildWorkRequestEmailSimple(solicitud);

      const response = await fetch(SEND_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          to: ["ptrabajo@cejm.eu", solicitud.requesterEmail].filter(Boolean),
          subject: `✅ [REENVÍO] Solicitud Parte - ${solicitud.codigoObra} - ${solicitud.requesterName}`,
          html: emailHtml,
        }),
      });

      const emailData = await response.json().catch(() => ({}));
      if (!response.ok || !emailData.ok) {
        throw new Error(emailData.error || `HTTP ${response.status}`);
      }

      await setDoc(doc(db, "work_requests", solicitud.id), {
        emailSentAt: serverTimestamp(),
        emailError: null,
        lastEmailResent: serverTimestamp(),
      }, { merge: true });

      showToast({ type: 'success', message: '✅ Email reenviado a ptrabajo@cejm.eu' });
      await loadSolicitudes();
    } catch (error) {
      console.error('❌ Error:', error);
      showToast({ type: 'error', message: `Error: ${error.message}` });
    } finally {
      setSendingEmail(null);
    }
  };

  // Helper: construir email completo (mismo que SolicitudParteTrabajo)
  const buildWorkRequestEmailSimple = (data) => {
    const baseUrl = import.meta.env.VITE_BASE_URL || 'https://firma-digital-db89b.web.app';
    const historialUrl = `${baseUrl}/historial-solicitudes`;
    
    const formatDate = (date) => {
      if (!date) return '-';
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    return `
      <div style="font-family:system-ui,sans-serif;font-size:15px;color:#111;max-width:600px;margin:0 auto;">
        <div style="background:#0b1028;padding:20px;border-radius:12px 12px 0 0;">
          <h1 style="color:#e9eefc;margin:0;font-size:24px;">🔧 SOLICITUD DE PARTE DE TRABAJO</h1>
          <p style="color:#94a3b8;margin:8px 0 0;font-size:14px;">✅ REENVÍO</p>
        </div>
        
        <div style="background:#fff;padding:24px;border-radius:0 0 12px 12px;">
          <div style="background:#f0f9ff;padding:16px;border-radius:8px;margin-bottom:20px;border-left:4px solid #4f6cf7;">
            <p style="margin:0 0 8px;"><strong>Solicitado por:</strong> ${data.requesterName || '-'}</p>
            <p style="margin:0 0 8px;"><strong>Email:</strong> ${data.requesterEmail || '-'}</p>
            <p style="margin:0;"><strong>Fecha solicitud:</strong> ${formatDate(data.createdAt)}</p>
          </div>

          <h3 style="color:#333;border-bottom:2px solid #eee;padding-bottom:8px;">DATOS DEL TRABAJO</h3>
          <table style="width:100%;margin-bottom:20px;">
            <tr><td style="padding:4px 0;"><strong>Fecha de inicio:</strong></td><td>${formatDate(data.fechaInicio)}</td></tr>
            <tr><td style="padding:4px 0;"><strong>Fecha de fin:</strong></td><td>${formatDate(data.fechaFin)}</td></tr>
            <tr><td style="padding:4px 0;"><strong>Rango de fechas:</strong></td><td>${data.rangoFechas || data.fechasActuacion || '-'}</td></tr>
            ${data.numeroOferta ? `<tr><td style="padding:4px 0;"><strong>Nº Oferta/Pedido:</strong></td><td>${data.numeroOferta}</td></tr>` : ''}
            ${data.numeroPresupuesto ? `<tr><td style="padding:4px 0;"><strong>Nº Presupuesto:</strong></td><td>${data.numeroPresupuesto}</td></tr>` : ''}
            <tr><td style="padding:4px 0;"><strong>Código de Obra:</strong></td><td>${data.codigoObra || '-'}</td></tr>
            <tr><td style="padding:4px 0;"><strong>Partida de Trabajo:</strong></td><td>${data.partidaTrabajo || '-'}</td></tr>
          </table>

          <h3 style="color:#333;border-bottom:2px solid #eee;padding-bottom:8px;">DESCRIPCIÓN</h3>
          <div style="background:#f9f9f9;padding:12px;border-radius:6px;margin-bottom:20px;">
            <p style="margin:0;white-space:pre-wrap;">${data.trabajosRealizar || data.descripcion || 'Sin descripción'}</p>
          </div>

          <h3 style="color:#333;border-bottom:2px solid #eee;padding-bottom:8px;">RECURSOS</h3>
          <p><strong>Operarios Asignados:</strong> ${data.operariosAsignados || '-'}</p>
          
          ${data.selectedOperarios && data.selectedOperarios.length > 0 ? `
          <div style="background:#f0f9ff;padding:12px;border-radius:6px;margin:12px 0;border-left:4px solid #4f6cf7;">
            <h4 style="margin:0 0 8px 0;color:#333;">👥 Equipo (${data.selectedOperarios.length}):</h4>
            <table style="width:100%;">
              ${data.selectedOperarios.map(op => `
                <tr>
                  <td style="padding:4px 0;"><strong>${op.nombre || op}</strong></td>
                  <td style="padding:4px 0;">${op.categoria || ''}</td>
                  <td style="padding:4px 0;">${op.email ? `<a href="mailto:${op.email}">${op.email}</a>` : ''}</td>
                </tr>
              `).join('')}
            </table>
          </div>
          ` : ''}

          <h3 style="color:#333;border-bottom:2px solid #eee;padding-bottom:8px;">UBICACIÓN</h3>
          <table style="width:100%;margin-bottom:30px;">
            <tr><td style="padding:4px 0;"><strong>Dirección:</strong></td><td>${data.direccionObra || '-'}</td></tr>
            <tr><td style="padding:4px 0;"><strong>Persona de Contacto:</strong></td><td>${data.personaContacto || '-'}</td></tr>
            ${data.telefonoContacto ? `<tr><td style="padding:4px 0;"><strong>Teléfono:</strong></td><td>${data.telefonoContacto}</td></tr>` : ''}
          </table>

          <div style="background:#e8f5e8;padding:20px;border-radius:8px;margin:20px 0;border-left:4px solid #10b981;text-align:center;">
            <h3 style="color:#065f46;margin:0 0 12px;">📋 GESTIONAR SOLICITUDES</h3>
            <p style="margin:0 0 16px;color:#047857;">Revisa el historial para crear partes de trabajo:</p>
            <a href="${historialUrl}" style="display:inline-block;background:#10b981;color:#fff;text-decoration:none;padding:14px 28px;border-radius:8px;font-weight:600;">
              📋 Ver Historial
            </a>
          </div>

          ${data.solicitarPRL ? `
          <div style="background:#fef3c7;padding:20px;border-radius:8px;margin:20px 0;border-left:4px solid #f59e0b;">
            <h3 style="color:#92400e;margin:0 0 12px;">🛡️ PRL SOLICITADO</h3>
            <p style="margin:0;color:#78350f;">Se requiere evaluación de PRL para esta obra.</p>
          </div>
          ` : ''}

          <hr style="border:none;border-top:1px solid #eee;margin:24px 0"/>
          <p style="color:#999;font-size:12px;margin:0;">Sistema CEJM - Reenvío automático</p>
        </div>
      </div>
    `;
  };

  // Formatear fecha
  const formatDate = (timestamp) => {
    if (!timestamp) return "Sin fecha";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const { paginated, page, pageSize, totalPages, totalItems, goTo, changePageSize } = usePagination(solicitudes);

  // Obtener color del estado
  const getStatusColor = (status) => {
    switch (status) {
      case 'enviada':    return { bg: '#fef3c7', color: '#92400e', border: '#fde68a' };
      case 'procesada':  return { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' };
      case 'completada': return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
      default:           return { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
    }
  };

  // Obtener texto del estado
  const getStatusText = (status) => {
    switch (status) {
      case 'enviada': return 'Enviada';
      case 'procesada': return 'En Proceso';
      case 'completada': return 'Completada';
      default: return 'Desconocido';
    }
  };

  if (loading) {
    return (
      <div className="container">
        <p style={{ padding: 24, textAlign: 'center' }}>Cargando historial...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <span className="badge">FIRMAS CEJM</span>
      </div>

      <h1 className="title">📋 Historial de Solicitudes</h1>

      <div className="card grid">
        {/* Filtros y acciones */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="meta">Filtrar por estado:</span>
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="todas">Todas</option>
              <option value="enviada">Enviadas</option>
              <option value="procesada">En Proceso</option>
              <option value="completada">Completadas</option>
            </select>
          </div>

          <button 
            className="btn" 
            onClick={() => navigate('/solicitar-parte')}
          >
            Nueva Solicitud
          </button>
        </div>

        {/* Lista de solicitudes */}
        {solicitudes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <p className="meta">No hay solicitudes que mostrar</p>
            <button className="btn" onClick={() => navigate('/solicitar-parte')} style={{ marginTop: 12 }}>
              Crear primera solicitud
            </button>
          </div>
        ) : (
          <div className="grid">
            {paginated.map((solicitud) => (
              <div 
                key={solicitud.id} 
                className="doc-card"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/solicitud/${solicitud.id}`)}
              >
                <div className="doc-card-header">
                  <h3 className="doc-card-title">
                    {solicitud.codigoObra} - {solicitud.partidaTrabajo}
                  </h3>
                  <span 
                    className="badge"
                    style={{ 
                      backgroundColor: getStatusColor(solicitud.status).bg,
                      color: getStatusColor(solicitud.status).color,
                      border: `1px solid ${getStatusColor(solicitud.status).border}`,
                      fontSize: '11px'
                    }}
                  >
                    {getStatusText(solicitud.status)}
                  </span>
                </div>

                <div className="doc-card-body">
                  <div className="doc-card-row">
                    <span className="meta">Fecha:</span>
                    <span className="doc-card-value">{formatDate(solicitud.createdAt)}</span>
                  </div>

                  <div className="doc-card-row">
                    <span className="meta">Fechas actuación:</span>
                    <span className="doc-card-value">{solicitud.fechasActuacion}</span>
                  </div>

                  {solicitud.numeroOferta && (
                    <div className="doc-card-row">
                      <span className="meta">Nº Oferta:</span>
                      <span className="doc-card-value">{solicitud.numeroOferta}</span>
                    </div>
                  )}

                  <div className="doc-card-row">
                    <span className="meta">Operarios:</span>
                    <span className="doc-card-value">{solicitud.operariosAsignados}</span>
                  </div>

                  <div className="doc-card-row">
                    <span className="meta">Contacto:</span>
                    <span className="doc-card-value">{solicitud.personaContacto}</span>
                  </div>

                  {/* 🛡️ Indicador PRL */}
                  {solicitud.solicitarPRL && (
                    <div className="doc-card-row" style={{ marginTop: '8px' }}>
                      <span style={{
                        background: solicitud.prlCompletado ? '#dcfce7' : '#fef3c7',
                        color: solicitud.prlCompletado ? '#065f46' : '#92400e',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        border: `1px solid ${solicitud.prlCompletado ? '#10b981' : '#f59e0b'}`
                      }}>
                        🛡️ PRL {solicitud.prlCompletado ? '✓ Completado' : '⏳ Pendiente'}
                      </span>
                    </div>
                  )}

                  {/* 📧 Indicador de Email */}
                  <div className="doc-card-row" style={{ marginTop: '8px' }}>
                    {solicitud.emailSentAt ? (
                      <span style={{
                        background: '#dcfce7',
                        color: '#065f46',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        border: '1px solid #10b981'
                      }}>
                        ✅ Email enviado
                      </span>
                    ) : (
                      <span style={{
                        background: '#fee2e2',
                        color: '#991b1b',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        border: '1px solid #ef4444'
                      }}>
                        ⚠️ Email NO enviado
                      </span>
                    )}
                  </div>
                </div>

                <div className="doc-card-actions">
                  <button 
                    className="copy"
                    onClick={(e) => { e.stopPropagation(); navigate(`/solicitud/${solicitud.id}`); }}
                  >
                    Ver Detalles
                  </button>

                  {/* Botón Reenviar Email - solo si NO se envió o es admin */}
                  {(!solicitud.emailSentAt || isAdmin) && (
                    <button 
                      className="btn"
                      onClick={(e) => handleReenviarEmail(e, solicitud)}
                      disabled={sendingEmail === solicitud.id}
                      style={{ 
                        backgroundColor: '#dc2626', 
                        borderColor: '#ef4444', 
                        fontSize: '12px', 
                        padding: '6px 12px',
                        opacity: sendingEmail === solicitud.id ? 0.5 : 1,
                        cursor: sendingEmail === solicitud.id ? 'wait' : 'pointer'
                      }}
                    >
                      {sendingEmail === solicitud.id ? '⏳ Enviando...' : '📧 Reenviar Email'}
                    </button>
                  )}
                  
                  <button 
                    className="btn"
                    onClick={(e) => { e.stopPropagation(); navigate(`/upload?from=solicitud&requestId=${solicitud.id}&type=parte_trabajo`); }}
                    style={{ backgroundColor: '#16a34a', borderColor: '#22c55e', fontSize: '12px', padding: '6px 12px' }}
                  >
                    🔧 Crear Parte
                  </button>

                  {/* Editar: usuario si está en "enviada", admin siempre */}
                  {(isAdmin || solicitud.status === 'enviada') && (
                    <button 
                      className="copy"
                      onClick={(e) => { e.stopPropagation(); navigate(`/solicitud/${solicitud.id}/editar`); }}
                      style={{ borderColor: '#f59e0b', color: '#f59e0b', fontSize: '12px', padding: '6px 12px' }}
                    >
                      ✏️ Editar
                    </button>
                  )}

                  {/* Eliminar: solo admins */}
                  {isAdmin && (
                    <button 
                      className="copy"
                      onClick={(e) => handleDelete(e, solicitud.id)}
                      style={{ borderColor: '#ef4444', color: '#ef4444', fontSize: '12px', padding: '6px 12px' }}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Información adicional */}
        <div style={{ marginTop: 20, padding: 16, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <p className="meta" style={{ margin: 0 }}>
            📊 Total de solicitudes: {solicitudes.length}
            {filter !== 'todas' && ` (filtradas por: ${getStatusText(filter)})`}
            {isAdmin && <span style={{ marginLeft: 8, color: '#f59e0b' }}>— Vista de administrador (todas las solicitudes de la empresa)</span>}
          </p>
          <Pagination page={page} pageSize={pageSize} totalPages={totalPages} totalItems={totalItems} goTo={goTo} changePageSize={changePageSize} />
        </div>
      </div>
    </div>
  );
}